<!--
 *@Author: 作者名 lx
 *@Date: 2026-01-10 10:38:49
 *@Description: 公共页面组件头部，页面
-->
<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useTableOfContents } from "@/composables/useTableOfContents"; // Import Table of Contents

type Theme = "light" | "dark";

const route = useRoute();
// 使用通用侧边栏状态
const { toggle: toggleGallerySidebar } = useSidebar('gallery');
const { headings, toggleMenu } = useTableOfContents(); // Use Table of Contents

// 使用 Composable 处理滚动隐藏逻辑
const headerContainer = ref<HTMLElement | null>(null);
const { isHidden } = useHeaderScroll(headerContainer);

// 使用 Composable 处理搜索逻辑
const { toggleSearch } = useSearch();

const colorScheme = useCookie<Theme | null>("color-scheme", { default: () => null });
const theme = ref<Theme>("light");

const systemTheme = (): Theme =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

const applyTheme = (value: Theme) => {
    theme.value = value;
    if (process.client) {
        document.documentElement.dataset.theme = value;
    }
};

onMounted(() => {

    // 1) 优先读取 head 脚本已设置好的主题（避免刷新时触发过渡）
    const domTheme = document.documentElement.dataset.theme;
    if (domTheme === "light" || domTheme === "dark") {
        theme.value = domTheme;
    }

    // 2) 如果存在手动 cookie，强制跟随 cookie；否则跟随系统
    if (colorScheme.value === "light" || colorScheme.value === "dark") {
        applyTheme(colorScheme.value);
    } else if (!domTheme) {
        applyTheme(systemTheme());
    }

    // 3) 仅在未手动设置时，系统主题变化才跟随
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", (e) => {
        if (!colorScheme.value) {
            applyTheme(e.matches ? "dark" : "light");
        }
    });





});

const setTheme = (value: Theme) => {
    if (theme.value === value) return;
    colorScheme.value = value;
    applyTheme(value);
};

const nextTheme = computed<Theme>(() => (theme.value === "light" ? "dark" : "light"));

const toggleTheme = () => {
    setTheme(nextTheme.value);
};

</script>

<template>
    <header class="headerContainer" :class="{ hidden: isHidden }" ref="headerContainer" role="banner">
        <NuxtLink class="page-title" to="/" aria-label="返回首页">
            LiXiang's Page
        </NuxtLink>

        <div class="header-menu-wrapper">
            <nav class="nav">
                <NuxtLink class="navLink" to="/gallery">图集</NuxtLink>
                <NuxtLink class="navLink" to="/archive">归档</NuxtLink>
                <NuxtLink class="navLink" to="/blog">博客</NuxtLink>
            </nav>
            <div class="header-right">
                <button type="button" class="searchBox" role="button" @click="toggleSearch"
                    :class="nextTheme === 'dark' ? 'next-dark' : 'next-light'" aria-label="搜索图标">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                        viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                        <path
                            d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                    </svg>
                </button>
                <div class="themeToggle" role="group" aria-label="主题切换">
                    <ClientOnly>
                        <button type="button" class="themeButton" :class="nextTheme === 'dark' ? 'next-dark' : 'next-light'"
                            :aria-label="`切换为${nextTheme === 'dark' ? '夜晚' : '白天'}模式`" @click="toggleTheme">

                            <svg v-if="nextTheme === 'dark'" class="themeIcon" viewBox="0 0 16 16" aria-hidden="true"
                                focusable="false" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                fill="currentColor">
                                <path
                                    d="M6 .278a.77.77 0 0 1 .08.858 7.2 7.2 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.79.79 0 0 1 .81.316.73.73 0 0 1-.031.893A8.35 8.35 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.75.75 0 0 1 6 .278M4.858 1.311A7.27 7.27 0 0 0 1.025 7.71c0 4.02 3.279 7.276 7.319 7.276a7.32 7.32 0 0 0 5.205-2.162q-.506.063-1.029.063c-4.61 0-8.343-3.714-8.343-8.29 0-1.167.242-2.278.681-3.286" />
                                <path
                                    d="M10.794 3.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387a1.73 1.73 0 0 0-1.097 1.097l-.387 1.162a.217.217 0 0 1-.412 0l-.387-1.162A1.73 1.73 0 0 0 9.31 6.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387a1.73 1.73 0 0 0 1.097-1.097zM13.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732l-.774-.258a.145.145 0 0 1 0-.274l.774.258c.346-.115.617-.386.732-.732z" />
                            </svg>

                            <svg v-else class="themeIcon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"
                                xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor">
                                <path
                                    d="M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0m0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13m8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5M3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8m10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0m-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0m9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707M4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708" />
                            </svg>
                        </button>
                        <template #fallback>
                            <div class="themeButton" style="width: 28px; height: 28px;"></div>
                        </template>
                    </ClientOnly>
                </div>
                <!-- TOC Button (只在小屏时显示，和画廊按钮同样逻辑) -->
                <button v-show="headings.length > 0" type="button" class="hamburger-menu toc-trigger" role="button" @click="toggleMenu"
                    aria-label="文章目录">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                        class="bi bi-list" viewBox="0 0 16 16">
                        <path fill-rule="evenodd"
                            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5" />
                    </svg>
                </button>
                <button v-show="route.path === '/gallery'" class="hamburger-menu gallery-trigger"
                    @click="toggleGallerySidebar" aria-label="分类菜单">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                        class="bi bi-list" viewBox="0 0 16 16">
                        <path fill-rule="evenodd"
                            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5" />
                    </svg>
                </button>

            </div>
        </div>
    </header>
</template>
<style scoped>
.hamburger-menu {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-color);
    padding: 0;

}

@media (max-width: 768px) {
    .hamburger-menu {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
        width: 24px;
    }
}

/* Gallery page specific breakpoint */
@media (max-width: 1536px) {
    .hamburger-menu.gallery-trigger,
    .hamburger-menu.toc-trigger {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
        width: 24px;
    }
}
</style>